// Sorting and grouping for the batch list.
//
// Pure functions, deliberately separate from the view so the ordering rules are
// testable without rendering — a wrong order is invisible on screen.
//
// # Group-by-coder, and the double-count it used to cause
//
// This file's header used to explain why there was no group-by-coder: a batch is
// not one person's work. AuditBatch.coders is an ARRAY, and the batches carrying
// the volume are worked by three to five coders, so grouping on that list puts
// one batch in several groups and every case total counts the same cases once
// per coder.
//
// That reasoning was right, and it is why the fix was not to relax it. The
// SERVER now sends per-coder case counts (Batch.coder_case_counts), so a batch
// can be attributed to the coder who did MOST of it — primaryCoderOf below. Each
// batch then lands in exactly one group, the counts sum to the list, and the
// concern the old header raised does not arise. Doug, 2026-08-26.
//
// The other half of that old warning still stands and is deliberately NOT
// resolved here: opening a batch shows ALL of its cases, not the primary
// coder's. Grouping is a way to find a batch, not a filter on what it contains.
// The case table inside a batch has its own group-by-coder (caseTable.ts), which
// is where a per-coder view of the work actually belongs.

import { coderIdentity } from "../data/coders";
import type { AuditBatch } from "../types";
import { assignmentLabel, type BatchAssignment } from "./queueAssignment";

/** Fields the batch list can sort by — one per table column. */
export type BatchField =
  | "batch"
  | "date"
  | "client"
  | "job"
  | "faCoding"
  | "closed"
  | "qcDue"
  | "cases"
  | "bothCoded"
  | "discrepancies"
  | "reviewed"
  | "assignee"
  | "coders"
  | "status";

/** Fields the batch list can group by. */
export type BatchGroupField = "date" | "client" | "assignee" | "coder";

export type SortDirection = "asc" | "desc";

export interface BatchColumn {
  field: BatchField;
  label: string;
  align?: "center";
  sortable: boolean;
  title?: string;
  /** Extra classes for the header cell, matching the body cell's width rules. */
  className?: string;
}

/**
 * The batch table's columns, in display order.
 *
 * The single source of truth for the header row, the sort dropdown and the sort
 * itself. Before this list existed the dropdown offered two fields while the
 * table showed fourteen columns, and nothing tied the two together — so twelve
 * columns looked sortable and were not.
 */
export const BATCH_COLUMNS: readonly BatchColumn[] = [
  { field: "batch", label: "Batch", sortable: true },
  { field: "date", label: "Date of service", sortable: true },
  { field: "client", label: "Client", sortable: true },
  { field: "job", label: "Job", sortable: true },
  // No Facility column: facility is per episode, a batch may span several, and
  // the batch-level value was empty on every row.
  { field: "faCoding", label: "FA coding", sortable: true, title: "Sorted by how many episodes FA has left to code." },
  // Dates FA's own coding batch list leads with — a coder plans around the QC
  // due date, and a closed date says when the batch stopped moving.
  { field: "closed", label: "Closed", sortable: true },
  { field: "qcDue", label: "QC due", sortable: true },
  { field: "cases", label: "Cases", align: "center", sortable: true },
  {
    field: "bothCoded",
    label: "Both coded",
    align: "center",
    sortable: true,
    title: "Cases coded by both a human coder and the auto-coder",
  },
  { field: "discrepancies", label: "Discrepancies", align: "center", sortable: true },
  {
    field: "reviewed",
    label: "Reviewed",
    sortable: true,
    title: "Sorted by the PROPORTION reviewed, not the raw count.",
    className: "min-w-[150px]",
  },
  { field: "assignee", label: "Assigned to", sortable: true },
  {
    field: "coders",
    label: "Coders",
    sortable: true,
    title: "Sorted by the coder who worked most of the batch.",
  },
  { field: "status", label: "Status", sortable: true },
];

/** Group-by options, in dropdown order. */
export const BATCH_GROUP_FIELDS: readonly {
  field: BatchGroupField;
  label: string;
}[] = [
  { field: "date", label: "Date" },
  { field: "client", label: "Client" },
  { field: "assignee", label: "Assignee" },
  { field: "coder", label: "Coder" },
];

/**
 * Context the assignee and coder rules need, which is not on the batch itself.
 *
 * Assignments live in the reviewer QUEUES, loaded separately from the batches —
 * a failure there degrades the assignment column rather than emptying the list,
 * so it cannot be folded into AuditBatch.
 */
export interface BatchGroupContext {
  /** From indexAssignments(queues). Empty when the queues have not loaded. */
  assignments: ReadonlyMap<string, BatchAssignment>;
}

/**
 * The coder who worked MOST of a batch, or null if none did.
 *
 * The whole basis of group-by-coder: it is what makes a batch belong to exactly
 * one group, so the group counts are true.
 *
 * Ties break on the coder ID, ascending, so a batch does not hop between groups
 * as the list refetches. That tie-break is also what carries the older-server
 * case: with no coder_case_counts every coder scores 0, they all tie, and the
 * batch lands under its lowest coder id — arbitrary but STABLE, and far better
 * than reporting every batch as uncoded, which would read as a data gap rather
 * than as a version skew.
 */
export function primaryCoderOf(
  batch: AuditBatch,
): { id: string; name: string } | null {
  if (batch.coders.length === 0) return null;

  let best = 0;
  for (let i = 1; i < batch.coders.length; i++) {
    const count = batch.coderCaseCounts[i] ?? 0;
    const bestCount = batch.coderCaseCounts[best] ?? 0;
    if (
      count > bestCount ||
      (count === bestCount && batch.coders[i] < batch.coders[best])
    ) {
      best = i;
    }
  }
  const id = batch.coders[best];
  // coderIdentity, not coderById: an unresolved FA id must not borrow a name
  // from the demo roster. It falls back to the id, which is what there is.
  return { id, name: coderIdentity(id, batch.coderNames[best]).name };
}

/** Who a batch is assigned to, as one label, or "" when nobody is. */
function assigneeOf(batch: AuditBatch, ctx: BatchGroupContext): string {
  return assignmentLabel(ctx.assignments.get(batch.id)) ?? "";
}

/**
 * The value a column sorts on.
 *
 * `undefined` means the batch has NO value for the column — not an empty one.
 * Load-bearing: an undefined value sorts last under BOTH directions, so undated
 * or unassigned batches collect at the end instead of leading the list whenever
 * the direction flips.
 *
 * Dates compare on their epoch-millisecond form, NEVER on the display string.
 * The live source formats those with toLocaleDateString, which yields "8/7/2026"
 * — and lexically that sorts AFTER "8/18/2026" because '7' > '1'. Re-parsing the
 * display string would not fix it either: a locale-formatted date is ambiguous
 * by construction (8/7 is August 7th or July 8th depending on the reader), so
 * the numeric field is carried alongside from the wire timestamp.
 */
function valueOf(
  b: AuditBatch,
  field: BatchField,
  ctx: BatchGroupContext,
): string | number | undefined {
  switch (field) {
    case "batch":
      return b.batchNumber || undefined;
    case "date":
      return b.dosSort || undefined;
    case "client":
      // jobCode is the client account in FA; client is the display name. Sort on
      // the code so two spellings of one client cannot split into two runs.
      return b.jobCode || b.client || undefined;
    case "job":
      return b.jobCode || undefined;
    case "faCoding":
      // What the column communicates is the SHORTFALL — how much FA has left to
      // code. A batch with nothing outstanding sorts as zero, not as absent:
      // "finished" is a real value on this column.
      return b.uncodedEpisodes;
    case "closed":
      return b.closeDateSort || undefined;
    case "qcDue":
      return b.qcDueDateSort || undefined;
    case "cases":
      return b.caseCount;
    case "bothCoded":
      return b.comparableCaseCount;
    case "discrepancies":
      return b.discrepancyCaseCount;
    case "reviewed":
      // A PROPORTION, because that is what the progress bar shows. Sorting on
      // the raw count puts 3 of 100 ahead of 1 of 2, which is the opposite of
      // the progress the column is reporting.
      return b.caseCount > 0 ? b.reviewedCount / b.caseCount : undefined;
    case "assignee":
      return assigneeOf(b, ctx) || undefined;
    case "coders":
      return primaryCoderOf(b)?.name ?? undefined;
    case "status":
      return b.state;
  }
}

const EMPTY_CONTEXT: BatchGroupContext = { assignments: new Map() };

function compareOn(
  a: AuditBatch,
  b: AuditBatch,
  field: BatchField,
  sign: number,
  ctx: BatchGroupContext,
): number {
  const av = valueOf(a, field, ctx);
  const bv = valueOf(b, field, ctx);
  // Absent sorts last regardless of direction, so `sign` is deliberately not
  // applied here. Reversing the order should bring the other end of the data to
  // the top, not a block of rows that have no value at all.
  if (av === undefined && bv === undefined) return 0;
  if (av === undefined) return 1;
  if (bv === undefined) return -1;
  const primary =
    typeof av === "number" && typeof bv === "number"
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true });
  return primary * sign;
}

/**
 * Sorts batches, newest first by default.
 *
 * The tiebreak on batchNumber is not cosmetic: without it, batches sharing a
 * value reorder between renders as the underlying array changes, and a reviewer
 * loses their place in a list they were scrolling.
 */
export function sortBatches(
  batches: readonly AuditBatch[],
  field: BatchField = "date",
  direction: SortDirection = "desc",
  ctx: BatchGroupContext = EMPTY_CONTEXT,
): AuditBatch[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...batches].sort((a, b) => {
    const primary = compareOn(a, b, field, sign, ctx);
    if (primary !== 0) return primary;
    // ASCENDING in both directions — the tiebreak exists for stability, and
    // flipping it with the sort would reshuffle tied rows on every toggle.
    return a.batchNumber.localeCompare(b.batchNumber, undefined, {
      numeric: true,
    });
  });
}

export interface BatchGroup {
  /** Stable key for React and for tests. */
  key: string;
  /** Numeric ordering value for the group — the batches' date for a date
   *  grouping, 0 otherwise. Never sort date groups on `key`: it is the display
   *  string, and ordering those is the defect this replaces. */
  sortKey: number;
  /** What the group header shows. */
  label: string;
  batches: AuditBatch[];
  /** Total cases across the group.
   *
   *  Meaningful because every grouping is a true PARTITION — each batch is in
   *  exactly one group — so these sum to the list's total. That is what
   *  primaryCoderOf buys: attributing a multi-coder batch to one coder rather
   *  than listing it under each. */
  caseCount: number;
}

/** Label for the bucket of batches with no value for the grouping field. */
const GROUP_NONE: Record<BatchGroupField, string> = {
  date: "(no date)",
  client: "(no client)",
  assignee: "Unassigned",
  coder: "Not coded yet",
};

function groupKeyOf(
  b: AuditBatch,
  field: BatchGroupField,
  ctx: BatchGroupContext,
): { key: string; label: string; sortKey: number } | null {
  switch (field) {
    case "date":
      return b.dos
        ? { key: b.dos, label: b.dos, sortKey: b.dosSort }
        : null;
    case "client": {
      const key = b.jobCode || b.client;
      if (!key) return null;
      return {
        key,
        label: b.client ? `${b.client} (${b.jobCode})` : b.jobCode,
        sortKey: 0,
      };
    }
    case "assignee": {
      const a = ctx.assignments.get(b.id);
      const label = assignmentLabel(a);
      if (!label) return null;
      // Keyed on the owning principal where there is one, so two reviewers who
      // share a display name do not merge into one group. A shared queue has no
      // single owner and keys on the queue instead.
      return { key: a?.ownerId || a?.queueId || label, label, sortKey: 0 };
    }
    case "coder": {
      const coder = primaryCoderOf(b);
      if (!coder) return null;
      // Keyed on the ID, labelled with the name — two coders can share a name.
      return { key: coder.id, label: coder.name, sortKey: 0 };
    }
  }
}

/**
 * Groups batches by a field.
 *
 * Every batch lands in exactly one group, so a group's caseCount is simply the
 * sum of its batches'. That holds for coder too, because a multi-coder batch is
 * attributed to its primary coder rather than listed under each — see this
 * file's header.
 *
 * Batches with no value for the field collect in a trailing group rather than
 * being filed under an arbitrary owner or dropped. For assignee that group is
 * the important one: unassigned work is what nobody is looking at.
 */
export function groupBatches(
  batches: readonly AuditBatch[],
  field: BatchGroupField,
  ctx: BatchGroupContext = EMPTY_CONTEXT,
): BatchGroup[] {
  const groups = new Map<string, BatchGroup>();
  const none: BatchGroup = {
    key: "__none__",
    sortKey: 0,
    label: GROUP_NONE[field],
    batches: [],
    caseCount: 0,
  };

  for (const batch of batches) {
    const g = groupKeyOf(batch, field, ctx);
    const target = g ? groups.get(g.key) : none;
    if (target) {
      target.batches.push(batch);
      target.caseCount += batch.caseCount;
      continue;
    }
    groups.set(g!.key, {
      key: g!.key,
      sortKey: g!.sortKey,
      label: g!.label,
      batches: [batch],
      caseCount: batch.caseCount,
    });
  }

  // Date groups order on the group's own sortKey, for the same reason compareOn
  // does — the key is the DISPLAY date, and sorting those strings is what put
  // 8/7 after 8/18. Everything else orders by label, so a reader can find a
  // name; a date view leading with the oldest day would bury today.
  const out = [...groups.values()];
  out.sort((a, b) =>
    field === "date"
      ? b.sortKey - a.sortKey
      : a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  // Last, always. It is the group whose members have only an absence in common,
  // and leading with it buries the work a reviewer came to do.
  if (none.batches.length > 0) out.push(none);
  return out;
}
